import { useNavigate } from "react-router-dom";
import { ArrowLeft, Home, LockKeyhole, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function UnauthorizedPage() {
  const navigate = useNavigate();

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }

  function handleGoHome() {
    navigate("/", { replace: true });
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border bg-muted/40">
              <ShieldAlert className="h-5 w-5 text-destructive" />
            </div>

            <div>
              <h1 className="text-xl font-bold tracking-tight">Acesso restrito</h1>
              <p className="text-sm text-muted-foreground">
                Seu perfil não possui permissão para acessar esta funcionalidade.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>

            <Button onClick={handleGoHome}>
              <Home className="mr-2 h-4 w-4" />
              Ir para o início
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-1 pb-10">
        <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center gap-2 text-base">
                <LockKeyhole className="h-4 w-4 text-primary" />
                Você chegou a uma área protegida
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold tracking-tight">
                  Esta página existe, mas não está liberada para o seu usuário.
                </h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  Isso normalmente acontece quando o seu perfil não possui a permissão
                  necessária para visualizar, editar ou administrar este recurso.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Motivo mais comum</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Seu grupo de acesso não contém a permissão exigida.
                  </p>
                </div>

                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Quando isso ocorre</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Ao abrir um link direto ou tentar entrar em uma área restrita.
                  </p>
                </div>

                <div className="rounded-xl border bg-card p-4">
                  <div className="text-sm font-medium">Como resolver</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Solicite ajuste de acesso ao administrador da sua empresa.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
                <div className="text-sm font-medium text-foreground">
                  Dica
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Caso você tenha chegado aqui por um favorito, link salvo ou URL digitada
                  manualmente, volte para o menu principal e acesse apenas as opções visíveis
                  para o seu perfil.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button onClick={handleGoHome}>
                  <Home className="mr-2 h-4 w-4" />
                  Voltar para a página inicial
                </Button>

                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retornar para a tela anterior
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base">O que você pode fazer agora</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4 p-6">
              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">1. Voltar</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Retorne para a tela anterior e continue navegando normalmente.
                </p>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">2. Ir para o início</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Acesse a home do sistema para encontrar módulos liberados para você.
                </p>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">3. Solicitar acesso</div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Se esta funcionalidade fizer parte da sua rotina, peça ao administrador
                  da empresa a revisão do seu perfil de acesso.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
